//  Drizzle docs
// https://orm.drizzle.team/docs/sql-schema-declaration
import { type AppState } from '@/lib/useAppState';
import { type AgentTrigger } from '@/lib/agentTriggers';
import { type ChannelConfig } from '@/lib/channelConfig';
import { type McpServerHeaders, type McpServerToolDescriptor } from '@/lib/mcpServerTypes';
import type { SecretEnvelope } from '@/lib/secretBox';
import { type ToolLog, type ViewActionDefinition } from '@/types';
import type { User } from '@/types';
import type { HistoryData } from '@/types/userHistoryEvents';
import { getTableColumns, sql } from 'drizzle-orm';
import {
	bigint,
	boolean, index,
	integer,
	jsonb,
	pgEnum,
	pgTable,
	primaryKey,
	type AnyPgColumn,
	type PgTableWithColumns,
	serial,
	text,
	timestamp,
	unique,
	uniqueIndex,
	uuid,
	varchar,
	vector
} from 'drizzle-orm/pg-core';

export const userTypeEnum = pgEnum('user_type', [
	'live',
	'sandbox',    // Temporary or synthetic users used for experimentation
	'discussion', // Discussion anchors
	'eval',
	'rated',      // Examples from thumbs up/down ratings
	'eval_run'    // One eval scenario answered by one agent version
]);
export const userStatus = pgEnum('status', ['active', 'unresponsive', 'success', 'error', 'dnc']); // @todo move to outreachStatus
export const discussionStatusEnum = pgEnum('discussion_status', [
	'open',
	'closed'
]);
export const testStatusEnum = pgEnum('test_status', [
	'sandbox',
	'error_severe',
	'error_moderate',
	'error_minor',
	'golden_example',       // Historical value
	'successful_example',
	'error_fixed',          // Historical value
	'error_recreated',      // Historical value
	'eval',
	'live',
	'neutral',
	'positive_example',
	'excellent_example'
]);
export const evalRunStatus = pgEnum('eval_run_status', ['pass', 'fail', 'running', 'error']);
export const reviewStatus = pgEnum('review_status', ['unread', 'good', 'bad', 'read']);

export type EvalRunStatus = typeof evalRunStatus.enumValues[number];
export type ReviewStatus = typeof reviewStatus.enumValues[number];
export type TestStatus = typeof testStatusEnum.enumValues[number];

// Workspace Multi-Tenancy Tables - Declared before users table

export const workspaces = pgTable('workspaces', {
	id: serial('id').primaryKey(),
	name: text('name').notNull(),
	slug: text('slug').notNull().unique(),
	settings: jsonb('settings').$type<{
		timezone?: string;
		requireGoogleLogin?: boolean;
		[key: string]: any;
	}>().default(sql`'{}'::jsonb`),
	isActive: boolean('is_active').default(true).notNull(),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

export type AgentAlertKind = 'provider_budget_exhausted';

/** One entry per (kind, key); a re-send replaces it, so the array stays as small as the set of distinct conditions. */
export interface AgentAlertRecord {
	kind: AgentAlertKind;
	/** What makes the condition distinct within its kind, e.g. `anthropic:agent-key`. */
	key: string;
	sentAt: string;
	detail?: Record<string, unknown>;
}

export interface AgentMeta {
	alerts?: AgentAlertRecord[];
}

/** A weighted split over whole versions; a conversation's pick becomes its pin (docs/impl/126). */
export interface AgentExperiment {
	versions: { id: number; weight: number }[];
	startedAt?: string;
}

/** One side of a metric: a saved grid view of this agent, narrowed by optional filters. */
export interface AgentMetricSide {
	viewId: number;
	filters?: { field: string; operator: string; value?: unknown }[];
	logicalOperator?: 'and' | 'or';
}

/** A named rate, numerator over denominator, both bucketed by `datetimeField` (docs/stats.md, "Metrics"). */
export interface AgentMetric {
	key: string;
	name: string;
	description?: string;
	numerator: AgentMetricSide;
	denominator: AgentMetricSide;
	datetimeField: string;
}

export const agents = pgTable('agents', {
	id: serial('id').primaryKey(),
	workspaceId: integer('workspace_id')
		.references(() => workspaces.id, { onDelete: 'cascade' })
		.notNull(),
	name: text('name').notNull(),
	slug: text('slug').notNull(),
	/** System-maintained state about the agent (alerts sent, notices). Not user-editable; that is `settings`. See docs/agents.md → agents.meta. */
	meta: jsonb('meta').$type<AgentMeta>().default(sql`'{}'::jsonb`).notNull(),
	/** Null when no experiment runs. `resolveAgentVersion` rolls on it for conversations with no pin. */
	experiment: jsonb('experiment').$type<AgentExperiment>(),
	/** Metric definitions, written only through `PATCH stats/metrics` (`src/services/MetricDefinitions.ts`). */
	metrics: jsonb('metrics').$type<AgentMetric[]>(),
	settings: jsonb('settings').$type<{
		prompts?: { systemPromptId?: number };
		tools?: string[];
		modelConfig?: { model: string; temperature: number };
		httpConfig?: {
			baseUrl: string;
			authToken?: string;
			endpoints: { chat: string; tools: string };
		};
		behavior?: {
			autoRespond: boolean;
			responseDelay: number;
		};
		[key: string]: any;
	}>().default(sql`'{}'::jsonb`).notNull(),
	// Set only on admin agents: the customer agent this admin agent serves (docs/impl/103).
	// AnyPgColumn breaks the self-reference circularity without widening the whole table type.
	targetAgentId: integer('target_agent_id')
		.references((): AnyPgColumn => agents.id, { onDelete: 'set null' }),
	isActive: boolean('is_active').default(true).notNull(),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	isActiveIdx: index('agents_is_active_idx').on(table.isActive),
	uniqueSlugPerWorkspace: uniqueIndex('agents_workspace_slug_unique')
		.on(table.workspaceId, table.slug),
	// One admin agent per target; doubles as the reverse-lookup index.
	uniqueTargetAgent: uniqueIndex('agents_target_agent_unique')
		.on(table.targetAgentId)
		.where(sql`target_agent_id IS NOT NULL`),
}));

export const users: PgTableWithColumns<any> = pgTable(
	'users',
	{
		id: serial('id').primaryKey(),
		agentId: integer('agent_id')
			.references(() => agents.id, { onDelete: 'cascade' })
			.notNull(),
		createdAt: timestamp('created').defaultNow(),
		lastResponseAt: timestamp('last_response'),
		// Keep this Drizzle-managed so schema.ts remains the source of truth and
		// explicit SQL backfills/tests can still set the column when needed.
		lastUpdatedAt: timestamp('last_updated_at')
			.defaultNow()
			.notNull()
			.$onUpdate(() => new Date()),
		recipientId: varchar('recipientId', { length: 512 }).notNull(),
		// Contact address for email delivery. Legacy Gmail-created users carry their address in
		// recipientId instead — read through emailForUser (src/lib/userContact.ts), not this column alone.
		email: varchar('email', { length: 512 }),
		priority: integer('priority').default(0),
		data: jsonb('data').$type<{
			name?: string;
			selected_office?: string;
			remote_consult?: boolean;
			patient_location?: string;
			appointment_time?: string;
			timezone?: string;
			tags?: string[]; // User tags for categorization (e.g., 'twilio-error-recovery', 'vip', etc.)
			[key: string]: any;
		}>(),
		// PRIMARY DISCRIMINATOR (repurposed field)
		userType: userTypeEnum('user_type').notNull(),
		testUser: boolean('test_user').default(false),
		testStatus: testStatusEnum('test_status'),
		status: userStatus('status').default('active'), // @todo rename to outreachStatus of a live user @todo depricate

		// discussions / tickets - RENAMED fields
		testDiscussionTitle: text('test_discussion_title'),
		testDiscussionTitleEmbedding: vector('test_discussion_title_embedding', { dimensions: 1536 }),
		testDiscussionStatus: discussionStatusEnum('test_discussion_status').default('open'),
		reviewStatus: reviewStatus('review_status').default('unread'),
		reviewNotes: text('review_notes'),
		reviewCreatedAt: timestamp('review_created_at'),
		reviewUpdatedAt: timestamp('review_updated_at'),
		testWhatWentWrong: text('test_what_went_wrong'), // @todo deprecate, no migration needed
		assignedTo: varchar('assigned_to', { length: 256 }), // Supabase auth user ID (UUID) - which auth user this discussion is assigned to
		discussionAuthor: varchar('discussion_author', { length: 256 }), // Supabase auth user ID (UUID) - the author who created this discussion

		testExpectedResponse: text('test_expected_response'),
		testSuccessCriteria: text('test_success_criteria'),
		testEvalTitle: text('test_eval_title'),
		testEvalTitleEmbedding: vector('test_eval_title_embedding', { dimensions: 1536 }),
		testClonedFromUserId: integer('test_cloned_from_userid').references(() => users.id, { onDelete: 'set null' }),
		testInitialUserId: integer('test_initial_userid').references(() => users.id, { onDelete: 'set null' }),
		// A "discussion user" is the user with the primary discussion; the URL can point other
		// selectedUserIds/discussionUserIds at it so different users show while the same ticket is open.
		testDiscussionUserId: integer('test_discussion_userid').references(() => users.id, { onDelete: 'set null' }),

		meta: jsonb('meta').$type<Record<string, any>>().default({}),

		// Set when this row lost a merge: the survivor it was folded into. Losers are tombstoned
		// rather than deleted so cached ids and old webhook payloads still resolve in one hop.
		mergedInto: integer('merged_into').references(() => users.id, { onDelete: 'set null' }),

		// Caller-owned key for mirrors of an external user base (public API `externalId`).
		externalId: varchar('external_id', { length: 256 }),
		// Soft lifecycle end: hidden from lists by default, still addressable by id.
		archivedAt: timestamp('archived_at'),
		// Suppression flag. Only UserRepo.setDoNotContact writes it; reason/source live in user_history.
		doNotContact: boolean('do_not_contact').default(false).notNull(),
	},
	(users) => {
		return {
			// Composite unique: same recipientId can exist across different agents
			recipientAgentIndex: uniqueIndex('recipient_agent_idx').on(users.recipientId, users.agentId),
			agentExternalIdIndex: uniqueIndex('users_agent_external_id_idx')
				.on(users.agentId, users.externalId)
				.where(sql`external_id IS NOT NULL`),
			// Serves `updatedSince` scans and keyset pagination on (agent, last_updated_at, id).
			agentLastUpdatedIndex: index('users_agent_last_updated_idx').on(users.agentId, users.lastUpdatedAt, users.id),
			lastResponseIndex: index('last_response_idx').on(users.lastResponseAt),
			// Serves the default table view: agent-scoped, sorted by last_response DESC NULLS LAST.
			// Column order and NULLS LAST must match the ORDER BY in searchUsersExtended or the
			// planner falls back to a seq scan + sort of every row in the agent.
			agentLastResponseIndex: index('users_agent_last_response_idx')
				.on(users.agentId, users.lastResponseAt.desc().nullsLast()),
			discussionTitleEmbeddingIndex: index('discussion_title_embedding_idx').using('hnsw', users.testDiscussionTitleEmbedding.op('vector_cosine_ops')),
			testInitialUserIdIndex: index('users_test_initial_userid_idx').on(users.testInitialUserId),
			testUserIndex: index('users_test_user_idx').on(users.testUser),
		};
	},
);

/**
 * One address a person is reachable at. Rows, not JSONB on `users`, because the routing invariant
 * is cross-row: one address resolves to one user per agent, which only a unique index can enforce.
 * `users.recipientId` / `users.email` stay as denormalised copies during the transition, each
 * mirrored here. See docs/impl/100-test-as-channel.
 */
export const userIdentities = pgTable('user_identities', {
	id: serial('id').primaryKey(),
	userId: integer('user_id')
		.references(() => users.id, { onDelete: 'cascade' })
		.notNull(),
	// Uniqueness is per-agent, matching recipient_agent_idx on users.
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	channelType: varchar('channel_type', { length: 32 }).notNull(),
	address: varchar('address', { length: 512 }).notNull(),
	verifiedAt: timestamp('verified_at'),
	source: varchar('source', { length: 32 }).notNull().default('inbound'),
	createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
	addressUnique: uniqueIndex('user_identities_agent_channel_address_idx')
		.on(table.agentId, table.channelType, table.address),
	userIdx: index('user_identities_user_id_idx').on(table.userId),
}));

export type UserIdentity = typeof userIdentities.$inferSelect;
export type NewUserIdentity = typeof userIdentities.$inferInsert;

export const userHistory = pgTable(
	'user_history',
	{
		id: serial('id').primaryKey(),
		agentId: integer('agent_id')
			.references(() => agents.id, { onDelete: 'cascade' })
			.notNull(),
		createdAt: timestamp('created')
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull(),
		userId: integer('user_id').references(() => users.id, { onDelete: 'cascade' }),
		event: varchar('event', { length: 256 }).notNull(),
		message: text('message'),
		// Caller-supplied idempotency key for imports, namespaced by the caller (`gmail:18c…`).
		externalId: varchar('external_id', { length: 256 }),
		data: jsonb('data').$type<
			HistoryData & {
				toolsLog?: ToolLog[];
				humanRating?: number; // 0 = bad, 1 = good
				humanRatingAuthor?: string;
				humanRatingCreatedAt?: Date;
			}
		>(),
	},
	(userHistory) => {
		return {
			event: index('event_idx').on(userHistory.event),
			agentExternalIdUnique: uniqueIndex('user_history_agent_external_id_idx')
				.on(userHistory.agentId, userHistory.externalId)
				.where(sql`external_id IS NOT NULL`),
			// Composite index for time-range queries with event filtering
			createdEventIndex: index('created_event_idx').on(userHistory.createdAt, userHistory.event),
			// Composite index for efficient user history lookups with sorting by created
			userIdCreatedIndex: index('user_id_created_idx').on(userHistory.userId, userHistory.createdAt),
			// Twilio status callbacks look rows up by message id; without this each one scanned the table (~750 ms).
			messageSidIdx: index('user_history_message_sid_idx')
				.on(sql`(${userHistory.data}->>'messageSid')`)
				.where(sql`${userHistory.data}->>'messageSid' IS NOT NULL`),
			// Note the index applied in 022_view_action_definition_idx.sql
		};
	},
);

export type UserHistoryData = typeof userHistory.$inferSelect['data'];

export type { User, LiveUser, SandboxUser, DiscussionUser, EvalUser, RatedUser, EvalRunUser } from '@/types';
export { isLiveUser, isSandboxUser, isDiscussionUser, isEvalUser, isRatedUser, isEvalRunUser, isTestUser } from '@/types';

export type DrizzleUser = typeof users.$inferSelect;
export type NewUser = typeof users.$inferInsert;

// The user as seen after flattenUser() (useAppState) promotes user.data.* fields to top level;
// use in render functions and UI components that receive flattened users.
export type UserFlattened = Omit<User, 'data'> & {
	// Common JSONB fields from user.data (non-exhaustive, see JSONB type for full spec)
	evalPass?: boolean;
	evalRunStatus?: EvalRunStatus;
	evalConfidence?: number;
	evalReason?: string;
	evalEvaluatedAt?: string;
	evalLastRunBy?: string;
	evalSuccessCriteria?: string;
	evalRegeneratedResponse?: string;
	evalAgentConfig?: any;
	name?: string;
	selected_office?: string;
	remote_consult?: boolean;
	patient_location?: string;
	appointment_time?: string;
	timezone?: string;
	tags?: string[];
	[key: string]: any;
};

export type UserHistory = typeof userHistory.$inferSelect;
export type NewUserHistory = typeof userHistory.$inferInsert;

// RLS policy (migration 0013_add_comments_rls_policy.sql) allows authenticated SELECT —
// required for Supabase realtime subscriptions to work.
export const comments = pgTable('comments', {
	id: serial('id').primaryKey(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),

	linkedUserId: integer('linked_user_id')
		.references(() => users.id, { onDelete: 'cascade' })
		.notNull(),

	// One level only: a reply's parent is always a top-level comment.
	parentCommentId: integer('parent_comment_id').references((): AnyPgColumn => comments.id, { onDelete: 'cascade' }),

	authorId: varchar('author_id', { length: 256 }).notNull(),

	content: text('content').notNull(),

	isEdited: boolean('is_edited').default(false).notNull(),
	isDeleted: boolean('is_deleted').default(false).notNull(),

	metadata: jsonb('metadata').$type<{
		mentions?: string[];
		attachments?: string[];
		[key: string]: any;
	}>(),
});

export type Comment = typeof comments.$inferSelect;
export type NewComment = typeof comments.$inferInsert;

export const workspaceSystemUsers = pgTable('workspace_system_users', {
	id: serial('id').primaryKey(),
	workspaceId: integer('workspace_id')
		.references(() => workspaces.id, { onDelete: 'cascade' })
		.notNull(),
	systemUserId: uuid('system_user_id').notNull(), // References auth.users
	role: text('role', { enum: ['workspace_superadmin', 'workspace_owner', 'workspace_member'] }).notNull(),
	invitationStatus: text('invitation_status', {
		enum: ['pending', 'accepted', 'declined']
	}).default('accepted').notNull(),
	invitedBy: uuid('invited_by'),
	invitedAt: timestamp('invited_at').defaultNow(),
	acceptedAt: timestamp('accepted_at'),
	declinedAt: timestamp('declined_at'),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	systemUserIdIdx: index('wsu_system_user_id_idx').on(table.systemUserId),
	roleIdx: index('wsu_role_idx').on(table.role),
	uniqueMembership: uniqueIndex('wsu_unique_membership')
		.on(table.workspaceId, table.systemUserId),
}));

export const agentSystemUsers = pgTable('agent_system_users', {
	id: serial('id').primaryKey(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	systemUserId: uuid('system_user_id').notNull(), // References auth.users
	role: text('role', {
		enum: ['agent_superadmin', 'agent_admin', 'agent_member']
	}).notNull(),
	invitationStatus: text('invitation_status', {
		enum: ['pending', 'accepted', 'declined']
	}).default('accepted').notNull(),
	invitedBy: uuid('invited_by'),
	invitedAt: timestamp('invited_at').defaultNow(),
	acceptedAt: timestamp('accepted_at'),
	declinedAt: timestamp('declined_at'),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	systemUserIdIdx: index('asu_system_user_id_idx').on(table.systemUserId),
	roleIdx: index('asu_role_idx').on(table.role),
	uniqueMembership: uniqueIndex('asu_unique_membership')
		.on(table.agentId, table.systemUserId),
}));

/** A named list of grants (`src/lib/rbac.ts`). Built-in roles have no workspace and are upserted by `RoleRepo.upsertBuiltinRoles`. */
export const roles = pgTable('roles', {
	id: serial('id').primaryKey(),
	workspaceId: integer('workspace_id').references(() => workspaces.id, { onDelete: 'cascade' }),
	key: text('key').notNull(),
	name: text('name').notNull(),
	description: text('description').notNull().default(''),
	grants: jsonb('grants').$type<{ permission: string; paths?: string[] }[]>().notNull(),
	isBuiltin: boolean('is_builtin').notNull().default(false),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	uniqueKey: unique('roles_workspace_key_unique').on(table.workspaceId, table.key).nullsNotDistinct(),
}));

export type RoleRow = typeof roles.$inferSelect;

/** A person holds a role on one agent, or on every agent in the workspace when `agent_id` is null. */
export const roleAssignments = pgTable('role_assignments', {
	id: serial('id').primaryKey(),
	workspaceId: integer('workspace_id')
		.references(() => workspaces.id, { onDelete: 'cascade' })
		.notNull(),
	systemUserId: uuid('system_user_id').notNull(),
	roleId: integer('role_id')
		.references(() => roles.id)
		.notNull(),
	agentId: integer('agent_id').references(() => agents.id, { onDelete: 'cascade' }),
	createdBy: uuid('created_by'),
	createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
	// Built-in roles have no workspace, so the workspace is part of the key: one person can hold the same role for all agents in several workspaces.
	uniqueAssignment: unique('role_assignments_unique').on(table.workspaceId, table.systemUserId, table.roleId, table.agentId).nullsNotDistinct(),
	workspaceUserIdx: index('role_assignments_workspace_user_idx').on(table.workspaceId, table.systemUserId),
	roleIdx: index('role_assignments_role_idx').on(table.roleId),
}));

export type RoleAssignmentRow = typeof roleAssignments.$inferSelect;

export const prompts = pgTable(
	'prompts',
	{
		id: serial('id').primaryKey(),
		agentId: integer('agent_id')
			.references(() => agents.id, { onDelete: 'cascade' })
			.notNull(),
		createdAt: timestamp('created_at').defaultNow().notNull(),
		updatedAt: timestamp('updated_at').defaultNow().notNull(),

		type: varchar('type', { length: 256 }).notNull(), // ex: metab-agent, billing-agent, sentiment-column
		content: text('content').notNull(),
		// Base64 Yjs update holding the draft's collaborative text state; null once deployed.
		draftState: text('draft_state'),

		version: integer('version').notNull(),
		versionSlug: varchar('version_slug', { length: 256 }),
		isActive: boolean('is_active').default(false),

		description: text('description'),
		versionAuthor: varchar('version_author'), // References auth.users.id

		model: varchar('model', { length: 256 }), // ex: gpt-4o, gpt-4o-mini, gpt-3.5-turbo, etc
		modelSettings: jsonb('model_settings').$type<{
			temperature?: number;       // 0.0 - 2.0
			maxTokens?: number;         // Max tokens in response
			topP?: number;              // 0.0 - 1.0
			presencePenalty?: number;   // -2.0 - 2.0
			frequencyPenalty?: number;  // -2.0 - 2.0
			seed?: number;              // For reproducibility
			stop?: string[];            // Stop sequences
		}>(),

		typeDescription: text('type_description'),
		typeIcon: varchar('type_icon', { length: 256 }), // Lucide icon name

		// Agent-platform role of this prompt — see src/lib/promptKinds.ts. 'system' is the legacy
		// default for pre-taxonomy rows; kept as a column (not meta) because we filter by it.
		// All other agent-platform fields live in `meta` — see docs/impl/79.
		kind: varchar('kind', { length: 32 }).default('system').notNull(),

		meta: jsonb('meta').$type<{
			promptVersionColumnName?: string;
			superadminOnly?: boolean;
			// Admin-tier prompts only (kind='admin', docs/impl/103): which target-agent roles this
			// tier serves. Unset = catch-all default tier.
			assistantRoles?: ('agent_superadmin' | 'agent_admin' | 'agent_member')[];
			// Sidebar ordering for the prompt type, denormalized across all versions like
			// typeDescription/typeIcon. See updateTypesOrder in PromptRepo.
			sortOrder?: number;
			primaryAgent?: boolean; // If true, this is the primary agent prompt that is edited by default with system tools
			linkedDiscussions?: number[];
			// Marks a mutable working draft: edited in place, visible only to its versionAuthor until
			// shared, promoted to an immutable numbered version by POST /prompts/publish (Deploy).
			draft?: boolean;
			// A shared draft is listed for everyone with agent access and editable in place by any
			// admin (collaboratively, via Yjs). Still a draft: no version number until deployed.
			shared?: boolean;
			sharedOn?: string;
			baseVersion?: number; // The version this draft was created from (for optimistic concurrency)
			draftNumber?: number; // Shown as v{baseVersion}.{draftNumber}; assigned at fork time
			expectedLiveVersion?: number; // The live version expected when activating (for conflict detection)
			deployedOn?: string;
			// Agent-platform config, only meaningful when kind='agent' (docs/impl/79). Canonical shape
			// is ToolSelection in src/agentToolRegistry/types.ts (79b D7); kept inline to avoid a
			// schema → registry import.
			tools?: {
				static?: Record<string, Record<string, unknown>>;
				composio?: { actions: Record<string, Record<string, unknown>> };
				mcp?: { tools: Record<string, Record<string, unknown>> };
			};
			// `parameters` is the effective (post-transform) schema, `baseParameters` the
			// pre-transform schema the editor un-pins from (79c C8).
			resolvedTools?: Array<{
				providerId: string;
				instanceKey?: string;
				toolId?: string;
				id: string;
				label: string;
				description: string;
				category?: string;
				sandboxPolicy?: 'preview' | 'execute';
				// Pre-100 cache rows; new saves write sandboxPolicy instead.
				liveness?: 'live-only' | 'always';
				connectedAccountId?: string;
				serverId?: number;
				parameters?: unknown;
				baseParameters?: unknown;
			}>;
			defaultMaxSteps?: number;
			outputSchema?: unknown;
			// What invokes this agent (docs/impl/91, Stage 2): an additive list of trigger instances
			// so one agent can hold two schedules. Absent means the pre-triggers default (row + bulk +
			// view worker); see src/lib/agentTriggers.ts. Channel bindings live on `channels` rows instead,
			// since those own credentials and workspace-level uniqueness a versioned prompt row can't enforce.
			triggers?: AgentTrigger[];
			[key: string]: any;
		}>(),
	},
	(prompts) => {
		return {
			versionIndex: index('version_idx').on(prompts.version),
			activeIndex: index('active_idx').on(prompts.isActive),
			typeVersionIndex: index('prompts_type_version_idx').on(prompts.type, prompts.version),
			typeActiveIndex: index('prompts_type_active_idx').on(prompts.type, prompts.isActive),
			agentIndex: index('prompts_agent_id_idx').on(prompts.agentId),
			// Unique constraint: only one active prompt per type per agent
			uniqueActivePerTypePerAgent: uniqueIndex('prompts_unique_active_per_type_per_agent')
				.on(prompts.agentId, prompts.type, prompts.isActive)
				.where(sql`${prompts.isActive} = true`),
		};
	}
);

export type PromptVersion = typeof prompts.$inferSelect;
export type NewPromptVersion = typeof prompts.$inferInsert;

export interface ModelSettings {
	temperature?: number;
	maxTokens?: number;
	topP?: number;
	presencePenalty?: number;
	frequencyPenalty?: number;
	seed?: number;
	stop?: string[];
}

// Manifest-driven agent versioning system.
export const agentVersions = pgTable('agent_versions', {
	id: serial('id').primaryKey(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),

	// Deployed number such as `v9`; null on drafts and candidates.
	version: text('version'),
	name: text('name').notNull(),
	description: text('description'),
	// Unused; kept so the old app keeps working until the post-rollout cleanup (drizzle/cleanup, 128 runbook).
	code: text('code'),

	status: text('status').$type<AgentVersionStatus>().notNull().default('version'),
	// The version's tree, path -> content (docs/impl/124-sandboxed-agent-versions).
	files: jsonb('files').$type<Record<string, string>>().notNull().default({}),
	// The version's Vercel deployment (docs/impl/125-agent-apps).
	deployment: jsonb('deployment').$type<AgentVersionDeployment>(),
	draftState: text('draft_state'),
	baseVersionId: integer('base_version_id').references((): AnyPgColumn => agentVersions.id, { onDelete: 'set null' }),
	draftNumber: integer('draft_number'),
	shared: boolean('shared').notNull().default(false),
	author: text('author'),
	linkedDiscussions: jsonb('linked_discussions').$type<number[]>(),
	checks: jsonb('checks').$type<AgentVersionChecks>(),
	submittedAt: timestamp('submitted_at'),
	promotedAt: timestamp('promoted_at'),

	type: text('type').notNull(), // 'internal' | 'external'
	agentDir: text('agent_dir'), // Filesystem directory for internal agents (e.g., 'CoreAgent')
	manifest: jsonb('manifest'), // NULL for internal agents (filesystem is authoritative), required for external

	baseUrl: text('base_url'),

	isLive: boolean('is_live').notNull().default(false),
	enabled: boolean('enabled').notNull().default(true),

	lastSyncedAt: timestamp('last_synced_at'),
	consecutiveSyncFailures: integer('consecutive_sync_failures').default(0),

	createdBy: text('created_by'),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	// Unique, not just indexed: two live rows leave `getLiveVersion` picking one at random, and the
	// UI hides "Set Live" on every live row, so there is no way back out from the app.
	agentVersionsLiveIdx: uniqueIndex('idx_agent_versions_one_live_per_agent')
		.on(table.agentId)
		.where(sql`${table.isLive} = true`),
	agentVersionsEnabledIdx: index('idx_agent_versions_enabled')
		.on(table.enabled)
		.where(sql`${table.enabled} = true`),
	uniqueVersionPerAgent: uniqueIndex('agent_versions_unique_version_per_agent')
		.on(table.agentId, table.version),
}));

export type AgentVersion = typeof agentVersions.$inferSelect;
export type NewAgentVersion = typeof agentVersions.$inferInsert;
/** `discarded` keeps a thrown-away draft's row so its draft number is never handed out again. */
export interface AgentVersionDeployment {
	projectId: string;
	deploymentId: string;
	/** Host of the deployment itself, e.g. `ts-agent-50-abc123.vercel.app`. */
	url: string;
	/** Hash of the tree minus `prompts/` and `knowledge/`, plus the SDK and the Vercel team; equal hashes share a build. */
	codeHash: string;
	state: 'building' | 'ready' | 'error';
	/** Production alias host, set while this version is live. */
	alias?: string;
	error?: string;
}

export type AgentVersionStatus = 'draft' | 'candidate' | 'version' | 'discarded';

export interface AgentVersionCheckResult {
	rule: string;
	ok: boolean;
	path?: string;
	message?: string;
}

export type AgentVersionCheckStatus = 'running' | 'done' | 'skipped' | 'error';

/** Something a candidate sends out that its base did not, shown at promote (src/versions/outbound.ts). */
export interface AgentVersionReviewNote {
	kind: 'host' | 'package' | 'usersLoop';
	value: string;
	path?: string;
}

export interface AgentVersionChecks {
	ranAt: string;
	/** Static checks pass and finished tool tests did not fail. Running steps are in `tests` and `evals`. */
	ok: boolean;
	static: AgentVersionCheckResult[];
	/** New outbound hosts, npm packages and `ctx.users` loops since the base. Informs; never blocks. */
	review?: AgentVersionReviewNote[];
	tests?: { ok: boolean; summary: string; status?: AgentVersionCheckStatus; ranAt?: string; output?: string };
	/** 0 to 1. */
	evalPassRate?: number;
	/** Filled from the version's `eval_run` users (docs/impl/121-eval-runs); `passed`/`total` count every scenario with a finished run. */
	evals?: {
		status: AgentVersionCheckStatus | 'not_run';
		/** Scenarios finished so far while running. */
		done?: number;
		passed?: number;
		total?: number;
		/** The live version's rate on the same scenarios, for comparison. */
		livePassRate?: number;
		livePassed?: number;
		liveTotal?: number;
		liveVersionId?: number;
		/** What the last run covered: a benchmark slug, or the listed scenarios. */
		benchmark?: string;
		scenarioIds?: number[];
		/** Eval users with success criteria when the run started. */
		scenarioCount?: number;
		startedAt?: string;
		message?: string;
	};
}

export const agentVersionKV = pgTable('agent_version_kv', {
	id: serial('id').primaryKey(),
	versionId: integer('version_id')
		.references(() => agentVersions.id, { onDelete: 'cascade' })
		.notNull(),
	key: varchar('key', { length: 256 }).notNull(),
	value: jsonb('value').notNull(),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	versionKeyUnique: uniqueIndex('agent_version_kv_version_key_unique')
		.on(table.versionId, table.key),
}));

export type AgentVersionKV = typeof agentVersionKV.$inferSelect;
export type NewAgentVersionKV = typeof agentVersionKV.$inferInsert;

/**
 * Channels — the numbers, inboxes and embeds an agent owns (docs/impl/91-composable-agents).
 * A channel belongs to an *agent*, not a workspace: every inbound entry point (Twilio webhook
 * slug, embed route, chat route) resolves its agent before any channel lookup, so scoping
 * uniqueness to the workspace let two agents collide on the same channel key. Operational state
 * that's written per inbound message lives in columns, not JSONB, to avoid read-modify-write
 * clobbering between concurrent messages on different channels.
 */
export const channels = pgTable('channels', {
	id: serial('id').primaryKey(),

	// Cascade rather than `set null`: an agent's embed or number has no meaning once the agent is gone.
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),

	// Denormalised from the owning agent, for RLS and the settings overview only — never identity.
	// ChannelRepo derives it from agentId so the two cannot drift.
	workspaceId: integer('workspace_id')
		.references(() => workspaces.id, { onDelete: 'cascade' })
		.notNull(),

	// Unused; kept so the old app keeps working until the post-rollout cleanup (drizzle/cleanup, 128 runbook).
	promptType: varchar('prompt_type', { length: 256 }),

	type: varchar('type', { length: 32 }).notNull(), // matches ChannelInstanceType
	channelKey: varchar('channel_key', { length: 256 }).notNull(),
	label: text('label'),
	enabled: boolean('enabled').default(true).notNull(),

	// Type-specific connection details, including encrypted secrets under `config.secrets`.
	config: jsonb('config').$type<ChannelConfig>().default(sql`'{}'::jsonb`).notNull(),

	verifiedAt: timestamp('verified_at'),
	lastInboundAt: timestamp('last_inbound_at'),
	consecutiveDeliveryFailures: integer('consecutive_delivery_failures').default(0).notNull(),

	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	// One endpoint answers to one binding, per agent — inbound must be deterministic.
	uniqueChannel: uniqueIndex('channels_agent_type_key_unique')
		.on(table.agentId, table.type, table.channelKey),
	workspaceIdx: index('channels_workspace_id_idx').on(table.workspaceId),
}));

export type Channel = typeof channels.$inferSelect;

/**
 * Operator-configured remote MCP servers, one row per server per agent (docs/impl/118). The
 * `mcp` tool provider reads these. `tools` caches the server's last tool list so the picker and
 * save validation never call the server; `enabledTools` is the additive exposure list. Header
 * values are encrypted under `headers.secrets`, masked under `headers.secretHints`.
 */
export const agentMcpServers = pgTable('agent_mcp_servers', {
	id: serial('id').primaryKey(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	// Denormalised from the owning agent, derived in McpServerRepo so it cannot drift.
	workspaceId: integer('workspace_id')
		.references(() => workspaces.id, { onDelete: 'cascade' })
		.notNull(),
	name: varchar('name', { length: 128 }).notNull(),
	url: text('url').notNull(),
	transport: varchar('transport', { length: 32 }).default('streamable-http').notNull(),
	headers: jsonb('headers').$type<McpServerHeaders>().default(sql`'{}'::jsonb`).notNull(),
	enabledTools: jsonb('enabled_tools').$type<string[]>().default(sql`'[]'::jsonb`).notNull(),
	tools: jsonb('tools').$type<McpServerToolDescriptor[]>().default(sql`'[]'::jsonb`).notNull(),
	// Tools on a read-only server default to sandboxPolicy 'execute' for Sandbox Users.
	readOnly: boolean('read_only').default(false).notNull(),
	active: boolean('active').default(true).notNull(),
	lastSyncedAt: timestamp('last_synced_at'),
	lastError: text('last_error'),
	createdBy: varchar('created_by', { length: 256 }),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	uniqueName: uniqueIndex('agent_mcp_servers_agent_name_unique').on(table.agentId, table.name),
	agentIdx: index('agent_mcp_servers_agent_id_idx').on(table.agentId),
	workspaceIdx: index('agent_mcp_servers_workspace_id_idx').on(table.workspaceId),
}));

export type AgentMcpServer = typeof agentMcpServers.$inferSelect;

/** One host an agent's `ctx.fetch` may reach, set by an agent admin, with an optional header the proxy adds (docs/tools.md, "Outbound hosts"). */
export const agentOutboundCredentials = pgTable('agent_outbound_credentials', {
	id: serial('id').primaryKey(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	workspaceId: integer('workspace_id')
		.references(() => workspaces.id, { onDelete: 'cascade' })
		.notNull(),
	host: varchar('host', { length: 253 }).notNull(),
	name: varchar('name', { length: 64 }),
	headerName: varchar('header_name', { length: 128 }),
	secret: jsonb('secret').$type<SecretEnvelope>(),
	secretHint: varchar('secret_hint', { length: 64 }),
	draftSecret: jsonb('draft_secret').$type<SecretEnvelope>(),
	draftSecretHint: varchar('draft_secret_hint', { length: 64 }),
	createdBy: varchar('created_by', { length: 256 }),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	uniqueHost: uniqueIndex('agent_outbound_credentials_agent_host_unique').on(table.agentId, table.host),
	uniqueName: uniqueIndex('agent_outbound_credentials_agent_name_unique').on(table.agentId, table.name),
	workspaceIdx: index('agent_outbound_credentials_workspace_id_idx').on(table.workspaceId),
}));

export type AgentOutboundCredential = typeof agentOutboundCredentials.$inferSelect;

export interface KnowledgeBaseConfig {
	embeddingModel?: string;
	chunkSize?: number;
	scoreThreshold?: number;
	maxNumResults?: number;
}

export interface KnowledgeBaseSyncState {
	lastJobId?: string;
	lastSyncAt?: string;
	lastSyncStatus?: 'pending' | 'running' | 'completed' | 'failed';
	/** Set when a sync job completes; absent means nothing has ever been indexed. */
	lastIndexedAt?: string;
	lastError?: string;
	fileCount?: number;
}

/**
 * Knowledge bases — workspace-owned document collections indexed by Cloudflare AI Search
 * (docs/knowledge-bases.md). Each row owns one R2 bucket and one AI Search instance; both names
 * are derived from stable row ids at provision time (never from the mutable slug) and stored so
 * teardown works even after a rename. Agents opt in via `agents.settings.knowledgeBaseIds`.
 */
export const knowledgeBases = pgTable('knowledge_bases', {
	id: serial('id').primaryKey(),
	workspaceId: integer('workspace_id')
		.references(() => workspaces.id, { onDelete: 'cascade' })
		.notNull(),
	name: varchar('name', { length: 256 }).notNull(),
	slug: varchar('slug', { length: 256 }).notNull(),
	// Shown to agents in the search tool's knowledge-base list, so it should say what's inside.
	description: text('description'),
	bucketName: varchar('bucket_name', { length: 63 }),
	cfInstanceId: varchar('cf_instance_id', { length: 64 }),
	status: varchar('status', { length: 32 }).default('provisioning').notNull(),
	config: jsonb('config').$type<KnowledgeBaseConfig>().default(sql`'{}'::jsonb`).notNull(),
	syncState: jsonb('sync_state').$type<KnowledgeBaseSyncState>().default(sql`'{}'::jsonb`).notNull(),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	uniqueSlug: uniqueIndex('knowledge_bases_workspace_slug_unique').on(table.workspaceId, table.slug),
}));

/**
 * Small expiring key/value counters (webhook dedup, daily send caps) — replaced Cloudflare KV.
 * Expired rows are treated as absent on read and lazily deleted on write; there is no reaper.
 */
export const kvEntries = pgTable('kv_entries', {
	key: varchar('key', { length: 512 }).primaryKey(),
	value: text('value').notNull(),
	expiresAt: timestamp('expires_at'),
});

export type KnowledgeBase = typeof knowledgeBases.$inferSelect;
export type KnowledgeBaseStatus = 'provisioning' | 'ready' | 'sync_pending' | 'syncing' | 'error' | 'deleting';

/**
 * When each cron trigger instance last fired (docs/impl/91-composable-agents). The trigger itself
 * lives in the versioned `prompts.meta.triggers`, so last-run state can't live beside it without a
 * draft save resetting the schedule; it's keyed by (agent, prompt type, trigger id) for the same reason.
 */
export const agentTriggerRuns = pgTable('agent_trigger_runs', {
	id: serial('id').primaryKey(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	promptType: varchar('prompt_type', { length: 256 }).notNull(),
	/** The instance key from `AgentTrigger.id`, so two schedules on one agent track separately. */
	triggerId: varchar('trigger_id', { length: 64 }).notNull(),
	lastRunAt: timestamp('last_run_at').notNull(),
	lastStatus: varchar('last_status', { length: 16 }),
	lastError: text('last_error'),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	uniqueTriggerRun: uniqueIndex('agent_trigger_runs_unique')
		.on(table.agentId, table.promptType, table.triggerId),
}));

export type AgentTriggerRun = typeof agentTriggerRuns.$inferSelect;
export type NewChannel = typeof channels.$inferInsert;

export const configTable = pgTable(
	'config_table',
	{
		id: serial('id').primaryKey(),
		agentId: integer('agent_id')
			.references(() => agents.id, { onDelete: 'cascade' })
			.notNull(),
		createdAt: timestamp('created_at').defaultNow().notNull(),
		updatedAt: timestamp('updated_at').defaultNow().notNull(),
		key: varchar('key', { length: 256 }).notNull(),
		value: jsonb('value').notNull(),
		isActive: boolean('is_active').default(true),
		previousValue: jsonb('previous_value'),
	},
	(configTable) => {
		return {
			keyIndex: index('key_idx').on(configTable.key),
			activeIndex: index('config_active_idx').on(configTable.isActive),
			uniqueActivePerAgent: uniqueIndex('config_unique_active_per_agent')
				.on(configTable.agentId, configTable.key)
				.where(sql`is_active = true`)
		};
	}
);

export type Config = typeof configTable.$inferSelect;
export type NewConfig = typeof configTable.$inferInsert;

export type ViewVisibility = 'public' | 'private';

export const views = pgTable(
	'views',
	{
		id: serial('id').primaryKey(),
		agentId: integer('agent_id')
			.references(() => agents.id, { onDelete: 'cascade' })
			.notNull(),
		createdAt: timestamp('created_at').defaultNow().notNull(),
		updatedAt: timestamp('updated_at').defaultNow().notNull(),
		name: text('name').notNull(),
		slug: text('slug').notNull(),
		icon: text('icon').notNull(),
		description: text('description'),
		kind: text('kind', { enum: ['grid', 'canvas'] }).notNull().default('grid'),
		// canvas only: the page body served through the sandboxed iframe (docs/custom-pages.md)
		html: text('html'),
		// canvas only: the shared artifact the page body is read from instead of `html` (docs/impl/135-artifacts)
		artifactId: integer('artifact_id').references((): any => assistantArtifacts.id, { onDelete: 'set null' }),
		searchParams: text('search_params').notNull(),
		appState: jsonb('app_state').default(sql`'{}'::jsonb`).$type<AppState>(),
		sortOrder: integer('sort_order').default(0).notNull(),
		viewActionDefinitions: jsonb('view_action_definitions')
			.default(sql`'[]'::jsonb`)
			.$type<ViewActionDefinition[]>(),
		// Sidebar nesting: a view can hold child views. Private views are visible only to their owner.
		parentId: integer('parent_id').references((): any => views.id, { onDelete: 'set null' }),
		// permissions: null means available to all users, otherwise array of role objects
		permissions: jsonb('permissions').$type<{ allowGroups: Array<'admin' | 'superadmin'> } | null>(),
		visibility: text('visibility', { enum: ['public', 'private'] }).notNull().default('public'),
		ownerSystemUserId: uuid('owner_system_user_id'), // References auth.users
		// Who made the view; they hold every view permission on it (docs/auth-permissions.md, "View rules").
		createdBy: uuid('created_by'),
	},
	(views) => {
		return {
			nameIndex: index('view_name_idx').on(views.name),
			slugIndex: uniqueIndex('views_unique_slug_per_agent').on(views.agentId, views.slug)
		};
	}
);

/**
 * Who may join a view's live-update channel (`view:<id>`), written by the Next.js app after its view
 * rule check and read by the `realtime.messages` policy. See docs/realtime.md, "View channels".
 */
export const viewLiveAccess = pgTable(
	'view_live_access',
	{
		systemUserId: uuid('system_user_id').notNull(),
		viewId: integer('view_id')
			.references(() => views.id, { onDelete: 'cascade' })
			.notNull(),
		agentId: integer('agent_id')
			.references(() => agents.id, { onDelete: 'cascade' })
			.notNull(),
		expiresAt: timestamp('expires_at').notNull(),
	},
	(table) => ({
		pk: primaryKey({ columns: [table.systemUserId, table.viewId] }),
		agentExpiresIdx: index('view_live_access_agent_expires_idx').on(table.agentId, table.expiresAt),
	})
);

// `html` is only read by the page route; list and by-id reads leave it out so the blob never
// travels with the views list. See ViewRepo.getCanvasViewBySlug.
export type DataView = Omit<typeof views.$inferSelect, 'html'> & { hasHtml: boolean };
export type NewDataView = typeof views.$inferInsert;
export type ViewKind = DataView['kind'];
export type CanvasView = DataView & { kind: 'canvas'; html: string };

// Unused; kept until the 128 cleanup migration drops it (docs/impl/128-prompt-rollout).
export const evalRuns = pgTable(
	'eval_runs',
	{
		id: serial('id').primaryKey(),
		agentId: integer('agent_id')
			.references(() => agents.id, { onDelete: 'cascade' })
			.notNull(),
		createdAt: timestamp('created_at').defaultNow().notNull(),
		systemPromptId: integer('system_prompt_id').references(() => prompts.id),
		agentVersionId: integer('agent_version_id').references(() => agentVersions.id, { onDelete: 'set null' }),
		userId: integer('user_id').references(() => users.id, { onDelete: 'cascade' }),
		status: evalRunStatus('status').default('running'),
	},
	(evalRuns) => {
		return {
			promptIndex: index('prompt_idx').on(evalRuns.systemPromptId),
			userIndex: index('eval_user_idx').on(evalRuns.userId),
			statusIndex: index('status_idx').on(evalRuns.status),
		};
	}
);

export interface OfficeLocation {
	name: string;
	Address: string;
	Phone: string | string[];
	Hours: string;
	region: string;
	timezone: string;
	email: string | string[];
	WhatsApp?: string | string[];
	/** Number of staff at this office. Used to determine how many remote appointments to route to this office (weighted random distribution). */
	staffCount?: number;
	/** Whether this office can receive remote appointments. Set to false to exclude from remote assignment. Defaults to true if not specified. */
	remoteAppointmentsEnabled?: boolean;
	isExpanded?: boolean;
	isNew?: boolean;
}

// API Tokens table (simple, no expiration)
export const apiTokens = pgTable('api_tokens', {
	id: serial('id').primaryKey(),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
	name: varchar('name', { length: 256 }).notNull(),
	// Legacy plaintext secret. Nullable because tokens issued after the hash-at-rest change never
	// populate it, and because a legacy row is cleared the first time its token is used. The unique
	// constraint stays in place: Postgres permits any number of NULLs under a unique index.
	token: varchar('token', { length: 64 }).unique(),
	tokenHash: varchar('token_hash', { length: 64 }), // SHA-256 of the token, lowercase hex
	tokenPrefix: varchar('token_prefix', { length: 16 }).notNull(), // First 16 chars for display
	isRevoked: boolean('is_revoked').default(false).notNull(),
	revokedAt: timestamp('revoked_at'),
	revokedBy: varchar('revoked_by', { length: 256 }),
	lastUsedAt: timestamp('last_used_at'),
	metadata: jsonb('metadata').$type<Record<string, any>>(),
	scopes: jsonb('scopes').$type<string[]>(),
	createdBy: varchar('created_by', { length: 256 }).notNull(),
	organizationId: varchar('organization_id', { length: 256 }),
	agentId: integer('agent_id').notNull().references(() => agents.id, { onDelete: 'cascade' }),
	viewId: integer('view_id').references(() => views.id, { onDelete: 'restrict' }),
	rateLimitWindowStart: timestamp('rate_limit_window_start'),
	rateLimitCount: integer('rate_limit_count').default(0).notNull(),
	// Per-token overrides; null falls back to the PUBLIC_API_V1_*_RATE_LIMIT_PER_MINUTE env defaults.
	rateLimitPerMinute: integer('rate_limit_per_minute'),
	batchRateLimitPerMinute: integer('batch_rate_limit_per_minute'),
	batchRateLimitWindowStart: timestamp('batch_rate_limit_window_start'),
	batchRateLimitCount: integer('batch_rate_limit_count').default(0).notNull(),
}, (apiTokens) => ({
	tokenPrefixIdx: index('api_tokens_token_prefix_idx').on(apiTokens.tokenPrefix),
	isRevokedIdx: index('api_tokens_is_revoked_idx').on(apiTokens.isRevoked),
	createdByIdx: index('api_tokens_created_by_idx').on(apiTokens.createdBy),
	organizationIdIdx: index('api_tokens_organization_id_idx').on(apiTokens.organizationId),
	agentIdIdx: index('api_tokens_agent_id_idx').on(apiTokens.agentId),
	viewIdIdx: index('api_tokens_view_id_idx').on(apiTokens.viewId)
}));

export type ApiToken = typeof apiTokens.$inferSelect;
export type NewApiToken = typeof apiTokens.$inferInsert;

export const auditLog = pgTable(
	'audit_log',
	{
		id: serial('id').primaryKey(),
		event: varchar('event', { length: 256 }).notNull(),
		data: jsonb('data').$type<Record<string, any>>(),
		date: timestamp('date').defaultNow().notNull(),
		expiryDate: timestamp('expiry_date').default(sql`CURRENT_TIMESTAMP + INTERVAL '1 year'`).notNull(),
		workspaceId: integer('workspace_id').references(() => workspaces.id, { onDelete: 'set null' }),
		agentId: integer('agent_id').references(() => agents.id, { onDelete: 'set null' }),
		userId: varchar('user_id', { length: 256 }), // Supabase auth user ID (UUID) - references auth.users
	},
	(table) => ({
		eventIdx: index('audit_log_event_idx').on(table.event),
		dateIdx: index('audit_log_date_idx').on(table.date),
		workspaceIdIdx: index('audit_log_workspace_id_idx').on(table.workspaceId),
		agentIdIdx: index('audit_log_agent_id_idx').on(table.agentId),
		userIdIdx: index('audit_log_user_id_idx').on(table.userId),
	}),
);

export type AuditLog = typeof auditLog.$inferSelect;
export type NewAuditLog = typeof auditLog.$inferInsert;

// Append-only ledger of LLM spend: one row per provider attempt, so a rate-limit retry is a second
// row rather than an amendment to the first. See docs/impl/99-llm-usage-ledger.
export const llmUsage = pgTable(
	'llm_usage',
	{
		id: serial('id').primaryKey(),
		// Minted before the call, so a nested call can name its parent and a retried write is a no-op.
		callId: uuid('call_id').notNull(),
		parentCallId: uuid('parent_call_id'),

		createdAt: timestamp('created_at').defaultNow().notNull(),

		// Deliberately not foreign keys, and no expiry column: a chargeback record has to outlive the
		// agent, user or workspace it came from.
		workspaceId: integer('workspace_id'),
		agentId: integer('agent_id'),
		versionId: integer('version_id'),
		subjectUserId: integer('subject_user_id'),
		actorUserId: varchar('actor_user_id', { length: 256 }), // Supabase auth uid, if a person triggered it

		source: varchar('source', { length: 64 }).notNull(),
		trigger: varchar('trigger', { length: 64 }),
		runId: uuid('run_id'), // groups one bulk or view-action run
		label: varchar('label', { length: 128 }), // action slug, prompt type, tool name
		promptId: integer('prompt_id'),
		// Outlives prompt rows: a prompt file in a version tree has a type but no row id.
		promptType: varchar('prompt_type', { length: 256 }),
		channel: varchar('channel', { length: 32 }),

		provider: varchar('provider', { length: 32 }).notNull(),
		model: varchar('model', { length: 128 }).notNull(), // stored id, groq/ prefix intact
		operation: varchar('operation', { length: 32 }).notNull(),
		attempt: integer('attempt').default(1).notNull(),
		ok: boolean('ok').default(true).notNull(),
		// Spend on an agent's own provider key is the customer's bill, not our COGS.
		billedTo: varchar('billed_to', { length: 16 }).default('platform').notNull(),
		latencyMs: integer('latency_ms'),

		inputTokens: integer('input_tokens').default(0).notNull(),
		cachedInputTokens: integer('cached_input_tokens').default(0).notNull(),
		cacheWriteTokens: integer('cache_write_tokens').default(0).notNull(),
		outputTokens: integer('output_tokens').default(0).notNull(),
		reasoningTokens: integer('reasoning_tokens').default(0).notNull(),
		totalTokens: integer('total_tokens').default(0).notNull(),

		// Nano-USD sums exactly and survives sub-micro-dollar calls; null, not 0, when there is no price.
		costNanoUsd: bigint('cost_nano_usd', { mode: 'number' }),
		priced: boolean('priced').default(false).notNull(),
		priceVersion: integer('price_version'),

		// The per-token rates actually applied, plus error class and provider request id.
		meta: jsonb('meta').$type<Record<string, any>>(),
	},
	(table) => ({
		callIdIdx: uniqueIndex('llm_usage_call_id_idx').on(table.callId),
		workspaceCreatedIdx: index('llm_usage_workspace_created_idx').on(table.workspaceId, table.createdAt),
		agentCreatedIdx: index('llm_usage_agent_created_idx').on(table.agentId, table.createdAt),
		runIdIdx: index('llm_usage_run_id_idx').on(table.runId),
		sourceIdx: index('llm_usage_source_idx').on(table.source),
	}),
);

export type LlmUsage = typeof llmUsage.$inferSelect;
export type NewLlmUsage = typeof llmUsage.$inferInsert;

// Unified webhook events table for idempotency and tracking, covering both inbound (from
// external services) and outbound (to external services) webhooks.
export const webhookEvents = pgTable('webhook_events', {
	id: serial('id').primaryKey(),

	direction: varchar('direction', { length: 10 }).notNull(), // 'inbound' | 'outbound'
	provider: varchar('provider', { length: 50 }).notNull(),   // 'stripe', 'twilio', 'custom', etc.

	externalEventId: varchar('external_event_id', { length: 255 }), // Provider's event ID (for inbound idempotency)
	internalEventId: varchar('internal_event_id', { length: 255 }), // Our event ID (for outbound tracking)

	eventType: varchar('event_type', { length: 100 }).notNull(),
	payload: jsonb('payload'),                                      // Full payload (optional, for debugging)

	status: varchar('status', { length: 20 }).notNull(),            // 'received', 'processing', 'processed', 'failed', 'pending', 'sent'
	statusMessage: text('status_message'),

	destinationUrl: varchar('destination_url', { length: 500 }),
	responseStatus: integer('response_status'),
	retryCount: integer('retry_count').default(0),
	nextRetryAt: timestamp('next_retry_at'),

	workspaceId: integer('workspace_id').references(() => workspaces.id, { onDelete: 'set null' }),
	agentId: integer('agent_id').references(() => agents.id, { onDelete: 'set null' }),
	webhookId: integer('webhook_id').references(() => webhooks.id, { onDelete: 'set null' }),

	createdAt: timestamp('created_at').defaultNow().notNull(),
	processedAt: timestamp('processed_at'),
	expiresAt: timestamp('expires_at'),  // For automatic cleanup
}, (table) => ({
	// Idempotency for inbound webhooks (provider + external ID must be unique)
	externalIdProviderIdx: uniqueIndex('webhook_external_id_provider_idx')
		.on(table.provider, table.externalEventId),
	// Query pending outbound webhooks
	directionStatusIdx: index('webhook_direction_status_idx')
		.on(table.direction, table.status),
	agentIdx: index('webhook_agent_idx').on(table.agentId),
	webhookIdx: index('webhook_events_webhook_id_idx').on(table.webhookId),
}));

export type WebhookEvent = typeof webhookEvents.$inferSelect;
export type NewWebhookEvent = typeof webhookEvents.$inferInsert;

// Webhook subscriptions: registers an external URL to receive outbound notifications when subscribed events fire.
export const webhooks = pgTable('webhooks', {
	id: serial('id').primaryKey(),
	workspaceId: integer('workspace_id').notNull().references(() => workspaces.id, { onDelete: 'cascade' }),
	agentId: integer('agent_id').notNull().references(() => agents.id, { onDelete: 'cascade' }),
	name: varchar('name', { length: 255 }).notNull(),
	url: varchar('url', { length: 1000 }).notNull(),
	eventTypes: jsonb('event_types').$type<string[]>().notNull(),
	userTypes: jsonb('user_types').$type<string[]>().notNull().default(sql`'["live"]'::jsonb`),
	secret: varchar('secret', { length: 128 }).notNull(),
	active: boolean('active').notNull().default(true),
	createdBy: varchar('created_by', { length: 256 }).notNull(),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
	lastTriggeredAt: timestamp('last_triggered_at'),
	lastFailureAt: timestamp('last_failure_at'),
	lastFailureMessage: text('last_failure_message'),
	// Projects user payloads for this subscriber; null → always-included fields plus changed values.
	viewId: integer('view_id').references(() => views.id, { onDelete: 'set null' }),
}, (table) => ({
	agentIdx: index('webhooks_agent_idx').on(table.agentId),
	activeIdx: index('webhooks_active_idx').on(table.active),
	viewIdx: index('webhooks_view_idx').on(table.viewId),
}));

export type Webhook = typeof webhooks.$inferSelect;
export type NewWebhook = typeof webhooks.$inferInsert;

// Stats cache: stores aggregated statistics for view queries.
export const stats = pgTable('stats', {
	id: serial('id').primaryKey(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	cacheKey: varchar('cache_key', { length: 512 }).notNull(),
	bucketDate: timestamp('bucket_date', { withTimezone: true }).notNull(),
	value: integer('value').notNull().default(0),
	createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
	updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
	uniqueCacheEntry: unique().on(table.agentId, table.cacheKey, table.bucketDate),
	cacheKeyPatternIdx: index('idx_stats_cache_key_pattern').on(table.cacheKey),
	createdAtIdx: index('idx_stats_created_at').on(table.createdAt),
}));

export type Stat = typeof stats.$inferSelect;
export type NewStat = typeof stats.$inferInsert;

// Embed sessions table - tracks anonymous visitor sessions for embedded chat widgets
export const embedSessions = pgTable('embed_sessions', {
	id: serial('id').primaryKey(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	workspaceId: integer('workspace_id')
		.references(() => workspaces.id, { onDelete: 'cascade' })
		.notNull(),
	visitorId: text('visitor_id').notNull(),
	visitorIdentity: jsonb('visitor_identity')
		.$type<{ name?: string; email?: string; externalId?: string }>()
		.default(sql`'{}'::jsonb`),
	metadata: jsonb('metadata')
		.$type<Record<string, unknown>>()
		.default(sql`'{}'::jsonb`),
	lastActiveAt: timestamp('last_active_at')
		.default(sql`CURRENT_TIMESTAMP`)
		.notNull(),
	createdAt: timestamp('created_at')
		.default(sql`CURRENT_TIMESTAMP`)
		.notNull(),
}, (table) => ({
	embedSessionsAgentIdIdx: index('embed_sessions_agent_id_idx').on(table.agentId),
	embedSessionsVisitorIdIdx: index('embed_sessions_visitor_id_idx').on(table.visitorId),
	embedSessionsWorkspaceIdIdx: index('embed_sessions_workspace_id_idx').on(table.workspaceId),
}));

export type EmbedSession = typeof embedSessions.$inferSelect;
export type NewEmbedSession = typeof embedSessions.$inferInsert;

// Unified logs table for client + server log capture
export const logs = pgTable('logs', {
	id: serial('id').primaryKey(),
	timestamp: timestamp('timestamp', { withTimezone: true }).notNull().defaultNow(),
	receivedAt: timestamp('received_at', { withTimezone: true }).notNull().defaultNow(),
	level: varchar('level', { length: 10 }).notNull(), // 'error' | 'warn' | 'info'
	source: varchar('source', { length: 50 }).notNull(), // 'client', 'server', 'client:fetch', 'client:boundary', etc.
	message: text('message').notNull(),
	stack: text('stack'),
	sessionId: varchar('session_id', { length: 256 }),
	data: jsonb('data').$type<Record<string, any>>(),
	expiresAt: timestamp('expires_at', { withTimezone: true }).notNull().default(sql`NOW() + INTERVAL '90 days'`),
}, (table) => ({
	timestampIdx: index('logs_timestamp_idx').on(table.timestamp),
	levelIdx: index('logs_level_idx').on(table.level),
	sessionIdIdx: index('logs_session_id_idx').on(table.sessionId),
	expiresAtIdx: index('logs_expires_at_idx').on(table.expiresAt),
}));

export type Log = typeof logs.$inferSelect;
export type NewLog = typeof logs.$inferInsert;

// Uses Object.keys() to get camelCase JS property names, not .name which returns snake_case DB names.
export const coreUserFields = Object.keys(getTableColumns(users));

export const coreUserFieldsSet = new Set(coreUserFields);

export function isCoreUserField(field: string): boolean {
	return coreUserFieldsSet.has(field);
}

// Text/varchar columns, used by is_null/is_not_null filter handling to treat empty strings as null.
const usersColumns = getTableColumns(users);
export const textUserColumns = new Set(
	Object.entries(usersColumns)
		.filter(([_, col]) => (col as any).dataType === 'text' || (col as any).dataType === 'varchar')
		.map(([key, _]) => key)
);

export function isTextUserColumn(field: string): boolean {
	return textUserColumns.has(field);
}

export type Workspace = typeof workspaces.$inferSelect;
export type NewWorkspace = typeof workspaces.$inferInsert;

export type Agent = typeof agents.$inferSelect;
export type NewAgent = typeof agents.$inferInsert;

export type WorkspaceSystemUser = typeof workspaceSystemUsers.$inferSelect;
export type NewWorkspaceSystemUser = typeof workspaceSystemUsers.$inferInsert;

export type AgentSystemUser = typeof agentSystemUsers.$inferSelect;
export type NewAgentSystemUser = typeof agentSystemUsers.$inferInsert;

/**
 * Spending limits. The most specific scope that has a limit overrides the broader ones outright
 * rather than combining with them — see `SCOPE_PRECEDENCE` in `LimitRepo`. Only `workspace` rows are
 * written today. `scope_id` points at a different table per scope, hence no foreign key.
 */
export const limits = pgTable('limits', {
	id: serial('id').primaryKey(),
	scope: text('scope', { enum: ['workspace', 'agent', 'user'] }).notNull(),
	scopeId: integer('scope_id').notNull(),
	kind: text('kind', { enum: ['llm_cost'] }).notNull(),
	period: text('period', { enum: ['calendar_month'] }).notNull().default('calendar_month'),
	/** Nano-USD, matching `llm_usage.cost_nano_usd`, so a comparison never crosses a float. */
	amountNanoUsd: bigint('amount_nano_usd', { mode: 'number' }).notNull(),
	enabled: boolean('enabled').notNull().default(true),
	createdBy: uuid('created_by'),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	limitsOnePerScopeKind: uniqueIndex('limits_one_per_scope_kind')
		.on(table.scope, table.scopeId, table.kind),
}));

/**
 * One row per threshold crossed per period, so each email goes out once rather than on every call
 * that stays over the line. The unique index is the guard; instances evaluate spend concurrently.
 */
export const limitNotifications = pgTable('limit_notifications', {
	id: serial('id').primaryKey(),
	limitId: integer('limit_id')
		.references(() => limits.id, { onDelete: 'cascade' })
		.notNull(),
	/** Start of the budget period, so thresholds re-arm when the month rolls over. */
	periodStart: timestamp('period_start').notNull(),
	threshold: integer('threshold').notNull(),
	sentAt: timestamp('sent_at').defaultNow().notNull(),
	recipientCount: integer('recipient_count'),
}, (table) => ({
	limitNotificationsOnce: uniqueIndex('limit_notifications_once')
		.on(table.limitId, table.periodStart, table.threshold),
}));

export type Limit = typeof limits.$inferSelect;
export type NewLimit = typeof limits.$inferInsert;
export type LimitNotification = typeof limitNotifications.$inferSelect;

/**
 * Stored responses for `Idempotency-Key` requests on the public API. A null response_status is a
 * request still in flight; rows expire 24h after creation.
 */
export const apiIdempotencyKeys = pgTable('api_idempotency_keys', {
	id: serial('id').primaryKey(),
	tokenId: integer('token_id')
		.references(() => apiTokens.id, { onDelete: 'cascade' })
		.notNull(),
	key: varchar('key', { length: 128 }).notNull(),
	requestHash: varchar('request_hash', { length: 64 }).notNull(),
	responseStatus: integer('response_status'),
	responseBody: jsonb('response_body'),
	responseHeaders: jsonb('response_headers').$type<Record<string, string>>(),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	expiresAt: timestamp('expires_at').default(sql`now() + interval '24 hours'`).notNull(),
}, (table) => ({
	tokenKeyIdx: uniqueIndex('api_idempotency_token_key_idx').on(table.tokenId, table.key),
	expiresIdx: index('api_idempotency_expires_idx').on(table.expiresAt),
}));

export type ApiIdempotencyKey = typeof apiIdempotencyKeys.$inferSelect;

/** The sandboxed assistant's `workspace/` folder (memory.md, notes) for one admin and agent, so it outlives the sandbox. */
export const assistantWorkspaces = pgTable('assistant_workspaces', {
	id: serial('id').primaryKey(),
	authUserId: text('auth_user_id').notNull(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	files: jsonb('files').$type<Record<string, string>>().notNull().default({}),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	userAgentIdx: uniqueIndex('assistant_workspaces_user_agent_idx').on(table.authUserId, table.agentId),
}));

export type AssistantWorkspace = typeof assistantWorkspaces.$inferSelect;

/** The sidebar assistant's `me/` notes: one person's notes for every agent. No agent key, so losing access to an agent keeps them. RLS on, no policies. */
export const assistantUserNotes = pgTable('assistant_user_notes', {
	id: serial('id').primaryKey(),
	authUserId: text('auth_user_id').notNull(),
	files: jsonb('files').$type<Record<string, string>>().notNull().default({}),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	userIdx: uniqueIndex('assistant_user_notes_user_idx').on(table.authUserId),
}));

/** The sidebar assistant's `shared/` notes and skills, read by every admin on the agent. Only an agent_superadmin's turn may change them. RLS on, no policies. */
export const assistantSharedNotes = pgTable('assistant_shared_notes', {
	id: serial('id').primaryKey(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	files: jsonb('files').$type<Record<string, string>>().notNull().default({}),
	updatedBy: text('updated_by'),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	agentIdx: uniqueIndex('assistant_shared_notes_agent_idx').on(table.agentId),
}));

/** Files an admin gave the sidebar assistant, one R2 object each. A null owner means shared with the agent. See docs/sandboxes.md, "Uploaded files". */
export const assistantFiles = pgTable('assistant_files', {
	id: serial('id').primaryKey(),
	workspaceId: integer('workspace_id')
		.references(() => workspaces.id, { onDelete: 'cascade' })
		.notNull(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	ownerAuthUserId: text('owner_auth_user_id'),
	name: text('name').notNull(),
	size: bigint('size', { mode: 'number' }).notNull(),
	mime: text('mime').notNull(),
	storageKey: text('storage_key').notNull(),
	sha256: text('sha256').notNull(),
	createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
	agentOwnerIdx: index('assistant_files_agent_owner_idx').on(table.agentId, table.ownerAuthUserId),
}));

export type AssistantFile = typeof assistantFiles.$inferSelect;

export type TimelineEventKind = 'version_live' | 'experiment_changed' | 'note';
export type TimelineEventSource = 'system' | 'assistant' | 'admin';

/** A dated change on one agent that may move a metric (docs/stats.md, "Timeline events"). RLS on, no policies. */
export const timelineEvents = pgTable('timeline_events', {
	id: serial('id').primaryKey(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	kind: text('kind').$type<TimelineEventKind>().notNull(),
	at: timestamp('at', { withTimezone: true }).notNull(),
	title: text('title').notNull(),
	description: text('description'),
	versionId: integer('version_id').references(() => agentVersions.id, { onDelete: 'set null' }),
	data: jsonb('data').$type<Record<string, unknown>>(),
	source: text('source').$type<TimelineEventSource>().notNull(),
	createdBy: text('created_by'),
	createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
	agentAtIdx: index('timeline_events_agent_at_idx').on(table.agentId, table.at),
}));

export type TimelineEvent = typeof timelineEvents.$inferSelect;
export type NewTimelineEvent = typeof timelineEvents.$inferInsert;

/**
 * Files the sidebar assistant made for a person to look at, one row per path in its `artifacts/` folder. The bytes
 * of each version are an R2 object; see docs/impl/135-artifacts. RLS on, no policies.
 */
export const assistantArtifacts = pgTable('assistant_artifacts', {
	id: serial('id').primaryKey(),
	// Names the artifact's R2 objects, so no two artifacts ever share one.
	uid: uuid('uid').defaultRandom().notNull(),
	workspaceId: integer('workspace_id')
		.references(() => workspaces.id, { onDelete: 'cascade' })
		.notNull(),
	agentId: integer('agent_id')
		.references(() => agents.id, { onDelete: 'cascade' })
		.notNull(),
	// 'comment': a file someone pasted into a discussion comment; kept out of the sandbox and the artifacts list.
	source: text('source', { enum: ['assistant', 'agent', 'comment'] }).notNull().default('assistant'),
	// The admin whose assistant wrote it. Null for an asset the agent made, which belongs to `userId`'s conversation.
	ownerAuthUserId: text('owner_auth_user_id'),
	userId: integer('user_id').references(() => users.id, { onDelete: 'cascade' }),
	path: text('path').notNull(),
	shared: boolean('shared').notNull().default(false),
	currentVersionId: integer('current_version_id').references((): AnyPgColumn => assistantArtifactVersions.id, { onDelete: 'set null' }),
	createdAt: timestamp('created_at').defaultNow().notNull(),
	updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
	ownerPathIdx: uniqueIndex('assistant_artifacts_agent_owner_path_idx').on(table.agentId, table.ownerAuthUserId, table.path),
	agentSharedIdx: index('assistant_artifacts_agent_shared_idx').on(table.agentId, table.shared),
}));

export type AssistantArtifact = typeof assistantArtifacts.$inferSelect;

/** One saved version of an artifact: an R2 object that is never overwritten. RLS on, no policies. */
export const assistantArtifactVersions = pgTable('assistant_artifact_versions', {
	id: serial('id').primaryKey(),
	artifactId: integer('artifact_id')
		.references((): AnyPgColumn => assistantArtifacts.id, { onDelete: 'cascade' })
		.notNull(),
	storageKey: text('storage_key').notNull(),
	sha256: text('sha256').notNull(),
	size: bigint('size', { mode: 'number' }).notNull(),
	mime: text('mime').notNull(),
	createdBy: text('created_by'),
	turnId: text('turn_id'),
	createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
	artifactIdx: index('assistant_artifact_versions_artifact_idx').on(table.artifactId),
}));

export type AssistantArtifactVersion = typeof assistantArtifactVersions.$inferSelect;
